import React from 'react';
import styled, { css } from 'styled-components';
import { AssetThumb, AssetThumbKind } from './AssetThumb';

export type BeatState = 'empty' | 'queued' | 'generating' | 'ready' | 'error';

export type BeatVariant = {
  id: string;
  kind?: AssetThumbKind;
  src?: string;
  posterSrc?: string;
};

export type BeatCardProps = {
  index: number;
  label?: string;
  caption?: string;
  state?: BeatState;
  variants?: BeatVariant[];
  activeVariantIndex?: number;
  active?: boolean;
  locked?: boolean;
  durationMs?: number;
  aspect?: '9:16' | '16:9' | '1:1' | '4:5';
  /** Optional row rendered below the variant bar (e.g. quality score chips). */
  metaSlot?: React.ReactNode;
  onSelect?: () => void;
  onVariantChange?: (variantIndex: number) => void;
  onRegenerate?: () => void;
  onToggleLock?: () => void;
  className?: string;
};

const Root = styled.div<{ $active: boolean; $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  border: 1px solid
    ${(p) => (p.$active ? 'var(--color-accent)' : 'var(--color-border-subtle)')};
  box-shadow: ${(p) => (p.$active ? '0 0 0 3px var(--color-focus-ring)' : 'var(--shadow-xs)')};
  width: 140px;
  flex-shrink: 0;
  transition:
    border-color var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard),
    transform var(--duration-base) var(--ease-standard);

  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      &:hover {
        border-color: var(--color-border-strong);
        transform: translateY(-1px);
      }
    `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 0 var(--space-1);
`;

const IndexLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.06em;
`;

const Duration = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-tertiary);
`;

const Caption = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: var(--line-height-base);
  padding: 0 var(--space-1);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(2 * var(--line-height-base) * var(--font-size-xs));
`;

const VariantBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1);
  padding: 0 var(--space-1);
`;

const ArrowButton = styled.button<{ $disabled?: boolean }>`
  appearance: none;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-pill);
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.4 : 1)};
  color: var(--color-text-secondary);
  font-size: 11px;
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover:not(:disabled) {
    background: var(--color-surface-inset);
    color: var(--color-text);
  }
`;

const VariantCount = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--color-text-secondary);
  letter-spacing: 0.04em;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
`;

const MetaSlot = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 var(--space-1);
`;

const IconButton = styled.button<{ $active?: boolean }>`
  appearance: none;
  background: ${(p) => (p.$active ? 'var(--color-accent-subtle)' : 'transparent')};
  border: 1px solid
    ${(p) => (p.$active ? 'var(--color-accent)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--color-accent-text)' : 'var(--color-text-secondary)')};
  border-radius: var(--radius-pill);
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-inset);
    color: var(--color-text);
  }
`;

const stop = (event: React.MouseEvent) => event.stopPropagation();

export const BeatCard: React.FC<BeatCardProps> = ({
  index,
  label,
  caption,
  state = 'empty',
  variants = [],
  activeVariantIndex = 0,
  active = false,
  locked = false,
  durationMs,
  aspect = '9:16',
  metaSlot,
  onSelect,
  onVariantChange,
  onRegenerate,
  onToggleLock,
  className,
}) => {
  const total = variants.length;
  const safeIndex = total === 0 ? 0 : Math.min(Math.max(0, activeVariantIndex), total - 1);
  const variant = variants[safeIndex];

  const handlePrev = (event: React.MouseEvent) => {
    stop(event);
    if (!onVariantChange || total < 2) return;
    onVariantChange((safeIndex - 1 + total) % total);
  };
  const handleNext = (event: React.MouseEvent) => {
    stop(event);
    if (!onVariantChange || total < 2) return;
    onVariantChange((safeIndex + 1) % total);
  };

  const durationSeconds = durationMs !== undefined ? (durationMs / 1000).toFixed(1) : null;

  return (
    <Root
      $active={active}
      $clickable={Boolean(onSelect)}
      onClick={onSelect}
      className={className}
    >
      <Header>
        <IndexLabel>{label ?? `BEAT ${String(index + 1).padStart(2, '0')}`}</IndexLabel>
        {durationSeconds && <Duration>{durationSeconds}s</Duration>}
      </Header>

      <AssetThumb
        kind={variant?.kind ?? 'image'}
        src={variant?.src}
        posterSrc={variant?.posterSrc}
        state={state}
        aspect={aspect}
        locked={locked}
        hoverPlay
      />

      {caption !== undefined && <Caption>{caption || ' '}</Caption>}

      <VariantBar>
        <ArrowButton
          type="button"
          aria-label="Previous variant"
          onClick={handlePrev}
          disabled={total < 2}
          $disabled={total < 2}
        >
          ◄
        </ArrowButton>
        <VariantCount>
          {total === 0 ? '—' : `${safeIndex + 1} / ${total}`}
        </VariantCount>
        <ArrowButton
          type="button"
          aria-label="Next variant"
          onClick={handleNext}
          disabled={total < 2}
          $disabled={total < 2}
        >
          ►
        </ArrowButton>

        <ActionRow>
          {onRegenerate && (
            <IconButton
              type="button"
              aria-label="Regenerate beat"
              onClick={(event) => {
                stop(event);
                onRegenerate();
              }}
            >
              ↻
            </IconButton>
          )}
          {onToggleLock && (
            <IconButton
              type="button"
              aria-label={locked ? 'Unlock beat' : 'Lock beat'}
              onClick={(event) => {
                stop(event);
                onToggleLock();
              }}
              $active={locked}
            >
              {locked ? '🔒' : '🔓'}
            </IconButton>
          )}
        </ActionRow>
      </VariantBar>

      {metaSlot && <MetaSlot onClick={stop}>{metaSlot}</MetaSlot>}
    </Root>
  );
};

BeatCard.displayName = 'BeatCard';
